import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type {
  IField,
  IFieldsTab,
  OtherInfoPanelCompletionIndicator,
  CompletionContextOverride,
} from '@moko/utils';
import { useOtherInfoContext } from '../OtherInfoContext';

interface InstanceLabel {
  label: string;
  panelIndex?: number;
  entityId?: number;
}

interface TabCompletion {
  percentage: number;
  isComplete: boolean;
  completedFields: number;
  totalFields: number;
}

interface MobileOtherInfoContextValue {
  tabs: IFieldsTab[];
  currentTabIndex: number;
  setCurrentTabIndex: (index: number) => void;
  selectTab: (index: number) => void | Promise<void>;
  goNext: () => void | Promise<void>;
  goPrevious: () => void | Promise<void>;
  showNavigation: boolean;
  setShowNavigation: (show: boolean) => void;
  getRepeatingPanelInstances: (panelName: string) => InstanceLabel[];
  isRepeatingPanel: (panelName: string) => { manual: boolean; entity: boolean };
  currentTab: IFieldsTab | undefined;
  totalTabs: number;
  completionPercentage: number;
  getPanelCompletionIndicator: (
    panelName: string,
    contextOverride?: CompletionContextOverride
  ) => OtherInfoPanelCompletionIndicator;
  getTabCompletion: (tab: IFieldsTab) => TabCompletion;
}

const MobileOtherInfoContext =
  createContext<MobileOtherInfoContextValue | null>(null);

export const useMobileOtherInfo = (): MobileOtherInfoContextValue => {
  const context = useContext(MobileOtherInfoContext);
  if (!context) {
    throw new Error(
      'useMobileOtherInfo must be used within MobileOtherInfoProvider'
    );
  }
  return context;
};

interface ProviderProps {
  tabs: IFieldsTab[];
  fields: IField[];
  children: React.ReactNode;
}

export const MobileOtherInfoProvider = ({
  tabs,
  fields,
  children,
}: ProviderProps) => {
  const {
    otherInfoObj,
    params,
    completionPercentage,
    getPanelCompletionIndicator,
  } = useOtherInfoContext();

  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [showNavigation, setShowNavigation] = useState(false);

  const isRepeatingPanel = useCallback(
    (panelName: string): { manual: boolean; entity: boolean } => {
      const panelField = params.metadata.fields.find(
        (f) => f.name === panelName
      );
      const mode = panelField?.repeatingMode?.toString()?.trim();
      return {
        manual: mode?.startsWith('M') ?? false,
        entity: mode?.startsWith('E') ?? false,
      };
    },
    [params.metadata.fields]
  );

  const getRepeatingPanelInstances = useCallback(
    (panelName: string): InstanceLabel[] => {
      const { manual, entity } = isRepeatingPanel(panelName);

      if (!manual && !entity) return [];

      if (manual) {
        const entries = Array.isArray(otherInfoObj[panelName])
          ? otherInfoObj[panelName]
          : [];
        if (entries.length === 0) return [];
        const panelField = fields.find((f) => f.name === panelName);
        const panelLabel = panelField?.label ?? panelName;
        return entries.map((_: unknown, i: number) => ({
          label: `${panelLabel} #${i + 1}`,
          panelIndex: i,
        }));
      }

      if (entity) {
        const relatedEntities = params.relatedEntities ?? [];
        return relatedEntities.map((e) => ({
          label: e.entityInfo.split('|').splice(1).join('|'),
          entityId: e.id,
        }));
      }

      return [];
    },
    [isRepeatingPanel, otherInfoObj, fields, params.relatedEntities]
  );

  const getTabCompletion = useCallback(
    (tab: IFieldsTab): TabCompletion => {
      const tabPanels = fields.filter(
        (f) =>
          f.tabName === tab.tabName &&
          f.fieldType === 'Panel' &&
          (f.parentName == null || f.parentName === 'root') &&
          f.effect !== 'Hide'
      );

      let completed = 0;
      let total = 0;

      for (const panel of tabPanels) {
        const { manual, entity } = isRepeatingPanel(panel.name);

        if (manual || entity) {
          for (const inst of getRepeatingPanelInstances(panel.name)) {
            const ind = getPanelCompletionIndicator(panel.name, {
              panelName: panel.name,
              ...(typeof inst.panelIndex === 'number' && {
                panelIndex: inst.panelIndex,
              }),
              ...(typeof inst.entityId === 'number' && {
                entityId: inst.entityId,
              }),
            });
            completed += ind.completedFields;
            total += ind.totalFields;
          }
        } else {
          const ind = getPanelCompletionIndicator(panel.name);
          completed += ind.completedFields;
          total += ind.totalFields;
        }
      }

      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
      return {
        percentage,
        isComplete: total > 0 && completed === total,
        completedFields: completed,
        totalFields: total,
      };
    },
    [
      fields,
      isRepeatingPanel,
      getRepeatingPanelInstances,
      getPanelCompletionIndicator,
    ]
  );

  const persistBeforeChange = useCallback(async () => {
    if (!params.persistForm) return true;
    return params.persistForm({ skipIfClean: true });
  }, [params.persistForm]);

  const goNext = useCallback(async () => {
    const ok = await persistBeforeChange();
    if (!ok) return;
    setCurrentTabIndex((i) => Math.min(i + 1, tabs.length - 1));
  }, [persistBeforeChange, tabs.length]);

  const goPrevious = useCallback(async () => {
    const ok = await persistBeforeChange();
    if (!ok) return;
    setCurrentTabIndex((i) => Math.max(i - 1, 0));
  }, [persistBeforeChange]);

  const selectTab = useCallback(
    async (index: number) => {
      setShowNavigation(false);
      if (index === currentTabIndex) return;
      const ok = await persistBeforeChange();
      if (!ok) return;
      setCurrentTabIndex(index);
    },
    [persistBeforeChange, currentTabIndex]
  );

  const value = useMemo(
    () => ({
      tabs,
      currentTabIndex,
      setCurrentTabIndex,
      selectTab,
      goNext,
      goPrevious,
      showNavigation,
      setShowNavigation,
      getRepeatingPanelInstances,
      isRepeatingPanel,
      currentTab: tabs[currentTabIndex],
      totalTabs: tabs.length,
      completionPercentage,
      getPanelCompletionIndicator,
      getTabCompletion,
    }),
    [
      tabs,
      currentTabIndex,
      selectTab,
      goNext,
      goPrevious,
      showNavigation,
      getRepeatingPanelInstances,
      isRepeatingPanel,
      completionPercentage,
      getPanelCompletionIndicator,
      getTabCompletion,
    ]
  );

  return (
    <MobileOtherInfoContext.Provider value={value}>
      {children}
    </MobileOtherInfoContext.Provider>
  );
};
